import { useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import PageSkeleton from "@/components/Loading/PageSkeleton";
import { ROUTES } from "@/constants";
import { getAdminSidebars } from "@/services";
import { useSelector } from "@/store";
import { AdminSidebar } from "@/types";
import { handleError } from "@/utils";

// Paths that bypass the per-row roleIds check entirely. /login is outside
// ProtectedRoute already, but it's listed for clarity. /403 must be excluded
// or the guard would loop redirecting to itself.
const EXCLUDED_PATHS = new Set<string>([ROUTES.LOGIN, ROUTES.FORBIDDEN, ROUTES.USER_PROFILE]);

const RoleGuard = () => {
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const roleId = user?.role ? String(user.role) : null;

  const [rows, setRows] = useState<AdminSidebar[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getAdminSidebars();
        if (!cancelled) setRows(data);
      } catch (error) {
        handleError(error);

        // Fail open on fetch error - bouncing the operator to 403 on a
        // transient API hiccup would lock them out of the whole panel.
        if (!cancelled) setRows([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // url → allowed role ids. Built once per fetch so the per-location lookup
  // below is a single Map.get.
  const rolesByUrl = useMemo(() => {
    const map = new Map<string, Set<string>>();
    if (!rows) return map;
    rows.forEach((row) => {
      if (!row.url) return;
      map.set(row.url, new Set((row.roleIds ?? []).map(String)));
    });

    return map;
  }, [rows]);

  if (rows === null) {
    return <PageSkeleton />;
  }

  // Excluded paths and paths not represented in the sidebar table pass
  // through - only registered URLs are gated.
  if (EXCLUDED_PATHS.has(location.pathname)) {
    return <Outlet />;
  }
  const allowedRoles = rolesByUrl.get(location.pathname);
  if (!allowedRoles) {
    return <Outlet />;
  }
  if (!roleId || !allowedRoles.has(roleId)) {
    return <Navigate to={ROUTES.FORBIDDEN} replace />;
  }

  return <Outlet />;
};

export default RoleGuard;
